import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocalOffer as CouponIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Coupon Management — App Admin controls for DigiDrop ad types/costs/worth bands,
 * global caps, and legal-safe spinner reveal UI. All values customizable (no redeploy).
 */
const DEFAULT_DISCOVERY = {
  smart_coupon_discovery_enabled: false,
  top_worth_count: 12,
  similar_enabled: true,
  notify_me_enabled: true,
  rewarded_ad_before_claim: true,
  processing_duration_ms: 2800,
  retry_limit: 3,
  max_similar: 5,
  default_citizen_radius_km: 2,
  max_citizen_radius_km: 25,
  default_mitra_service_radius_km: 10,
};

const CouponManagementAdmin = () => {
  const [tab, setTab] = useState(0);
  const [rules, setRules] = useState([]);
  const [ui, setUi] = useState(null);
  const [discovery, setDiscovery] = useState(DEFAULT_DISCOVERY);
  const [pool, setPool] = useState(null);
  const [poolQuery, setPoolQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const authHeaders = () => {
    const token =
      localStorage.getItem('admin_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('user_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, uiRes, discoveryRes, poolRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/business-posts/coupon-flow/rules`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/business-posts/coupon-flow/ui-settings`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/coupon-discovery/admin/settings`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/business-posts/coupon-flow/pool-inventory`, {
          headers: authHeaders(),
        }),
      ]);
      if (rulesRes.data?.success) {
        setRules(rulesRes.data.data?.rules || []);
      }
      if (uiRes.data?.success) {
        setUi(uiRes.data.data || null);
      }
      if (discoveryRes.data?.success && discoveryRes.data.data) {
        setDiscovery({ ...DEFAULT_DISCOVERY, ...discoveryRes.data.data });
      }
      if (poolRes.data?.success) {
        setPool(poolRes.data.data || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load Coupon Management');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Citizen Coupon Management - LOCSY';
    load();
  }, [load]);

  const updateRule = (placementKey, patch) => {
    setRules((prev) =>
      prev.map((r) =>
        r.placementKey === placementKey ? { ...r, ...patch } : r,
      ),
    );
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/business-posts/coupon-flow/rules`,
        { rules },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setRules(res.data.data?.rules?.length ? res.data.data.rules : rules);
        toast.success('Ad types saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveUi = async () => {
    if (!ui) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/business-posts/coupon-flow/ui-settings`,
        ui,
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setUi(res.data.data);
        toast.success('Spinner / UI settings saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveDiscovery = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/coupon-discovery/admin/settings`,
        discovery,
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setDiscovery({ ...DEFAULT_DISCOVERY, ...res.data.data });
        toast.success('Smart Coupon Discovery settings saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const patchDiscovery = (patch) =>
    setDiscovery((prev) => ({ ...prev, ...patch }));

  const addAdType = async () => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    const label = newLabel.trim() || key;
    if (!key) {
      toast.error('Enter a key (e.g. popup)');
      return;
    }
    if (rules.some((r) => r.placementKey === key)) {
      toast.error('Ad type already exists');
      return;
    }
    const next = [
      ...rules,
      {
        placementKey: key,
        label,
        adCost: 20,
        minCouponWorth: 0,
        couponsPerEngage: 1,
        dailyCitizenCap: 8,
        enabled: true,
        preferSameBusiness: true,
        preferSameTown: true,
        requireLogin: true,
        sortOrder: 100,
      },
    ];
    setRules(next);
    setNewKey('');
    setNewLabel('');
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/business-posts/coupon-flow/rules`,
        { rules: next },
        { headers: authHeaders() },
      );
      if (res.data?.success) {
        setRules(res.data.data?.rules || next);
        toast.success('Ad type added');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Add failed');
    } finally {
      setSaving(false);
    }
  };

  const deleteAdType = async (placementKey) => {
    if (!window.confirm(`Delete ad type "${placementKey}"?`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/business-posts/coupon-flow/rules/${encodeURIComponent(placementKey)}`,
        { headers: authHeaders() },
      );
      setRules((prev) => prev.filter((r) => r.placementKey !== placementKey));
      toast.success('Ad type deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <CouponIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Citizen Coupon Management
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Customize DigiDrop ad types, costs, worth bands, caps, and spinner reveal.
          Defaults favour Citizen + Mitra; App Admin can tune live without redeploy.
        </Typography>

        <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
          <Button
            component={RouterLink}
            to="/content/coupon-flow"
            size="small"
            variant="outlined"
          >
            Open Coupon Flow
          </Button>
          <Button
            component={RouterLink}
            to="/content/lucky-wheel"
            size="small"
            variant="outlined"
          >
            Open Locsy Spin
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            size="small"
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
          <Tab label="Distribution pool" />
          <Tab label="Ad types & costs" />
          <Tab label="Spinner & caps" />
          <Tab label="Smart Discovery" />
        </Tabs>

        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : tab === 0 ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Active <strong>Share with Locsy</strong> templates in the DigiDrop pool.
              Worth points are auto-calculated from offer savings (₹ user saves).
              For each ad type, allotment picks <strong>highest + lowest</strong> worth
              when giving 2 coupons (and alternates high/low for more).
            </Alert>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              mb={2}
              flexWrap="wrap"
              useFlexGap
            >
              {[
                {
                  label: 'In pool',
                  value: pool?.templateCount ?? '—',
                  hint: 'Active templates ready to distribute',
                },
                {
                  label: 'Unlimited',
                  value: pool?.unlimitedCount ?? '—',
                  hint: 'No digidrop qty cap',
                },
                {
                  label: 'Limited remaining',
                  value: pool?.limitedRemaining ?? '—',
                  hint: 'Sum of capped pool remaining',
                },
                {
                  label: 'Issued (all time)',
                  value: pool?.totalIssuedFromPool ?? '—',
                  hint: 'Clones issued from these templates',
                },
                {
                  label: 'Issued today',
                  value:
                    pool?.flowStats?.couponsIssuedToday ??
                    pool?.issuedTodayFromPool ??
                    '—',
                  hint: `Grants today: ${pool?.flowStats?.grantsToday ?? '—'} · ${
                    pool?.grantDate || pool?.flowStats?.grantDate || 'IST'
                  }`,
                },
                {
                  label: 'Avg worth',
                  value: pool?.avgWorth != null ? `₹${pool.avgWorth}` : '—',
                  hint: 'Mean coupon_worth in pool',
                },
              ].map((card) => (
                <Paper key={card.label} sx={{ p: 2, minWidth: 150, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.hint}
                  </Typography>
                </Paper>
              ))}
            </Stack>

            {!!pool?.flowStats?.byPlacement?.length && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography fontWeight={700} mb={1}>
                  Today by placement
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {pool.flowStats.byPlacement.map((row) => (
                    <Chip
                      key={row.placement}
                      size="small"
                      label={`${row.placement}: ${row.grants} grants · ${row.coupons} coupons`}
                    />
                  ))}
                </Stack>
              </Paper>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              mb={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography fontWeight={700}>
                Coupons to distribute ({(pool?.coupons || []).length})
              </Typography>
              <TextField
                size="small"
                placeholder="Search name, code, town, business…"
                value={poolQuery}
                onChange={(e) => setPoolQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
            </Stack>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Coupon</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell align="right">Worth</TableCell>
                    <TableCell>Business / Town</TableCell>
                    <TableCell align="right">Pool qty</TableCell>
                    <TableCell align="right">Remaining</TableCell>
                    <TableCell align="right">Issued</TableCell>
                    <TableCell align="right">Today</TableCell>
                    <TableCell>Valid until</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pool?.coupons || [])
                    .filter((c) => {
                      const q = poolQuery.trim().toLowerCase();
                      if (!q) return true;
                      return [
                        c.name,
                        c.code,
                        c.businessName,
                        c.townName,
                        c.serviceName,
                      ]
                        .filter(Boolean)
                        .some((v) => String(v).toLowerCase().includes(q));
                    })
                    .map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {c.name || '—'}
                          </Typography>
                          {c.serviceName && (
                            <Typography variant="caption" color="text.secondary">
                              {c.serviceName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            {c.code || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {c.worth != null ? `₹${c.worth}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {c.businessName || c.businessId || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.townName || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {c.unlimited ? (
                            <Chip size="small" label="∞" />
                          ) : (
                            c.poolQty ?? '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {c.unlimited ? '∞' : c.poolRemaining ?? '—'}
                        </TableCell>
                        <TableCell align="right">{c.issuedTotal ?? 0}</TableCell>
                        <TableCell align="right">{c.issuedToday ?? 0}</TableCell>
                        <TableCell>
                          {c.validUntil
                            ? String(c.validUntil).slice(0, 10)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  {!(pool?.coupons || []).length && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography color="text.secondary" py={2} textAlign="center">
                          No Share-with-Locsy coupons in the distribution pool yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : tab === 1 ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Worth is auto-calculated from each offer&apos;s savings.{" "}
              <strong>Per engage</strong> sets how many coupons to give — e.g. 2 =
              one highest-worth + one lowest-worth from the DigiDrop pool. Min worth
              is a soft floor only.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Min worth</TableCell>
                  <TableCell>Per engage</TableCell>
                  <TableCell>Daily cap</TableCell>
                  <TableCell>On</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.placementKey}>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {r.placementKey}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={r.label || ''}
                        onChange={(e) =>
                          updateRule(r.placementKey, { label: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.adCost ?? 0}
                        onChange={(e) =>
                          updateRule(r.placementKey, {
                            adCost: Number(e.target.value),
                          })
                        }
                        sx={{ width: 80 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.minCouponWorth ?? 0}
                        onChange={(e) =>
                          updateRule(r.placementKey, {
                            minCouponWorth: Number(e.target.value),
                          })
                        }
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.couponsPerEngage ?? 1}
                        onChange={(e) =>
                          updateRule(r.placementKey, {
                            couponsPerEngage: Number(e.target.value),
                          })
                        }
                        sx={{ width: 70 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.dailyCitizenCap ?? 8}
                        onChange={(e) =>
                          updateRule(r.placementKey, {
                            dailyCitizenCap: Number(e.target.value),
                          })
                        }
                        sx={{ width: 70 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={r.enabled !== false}
                        onChange={(e) =>
                          updateRule(r.placementKey, {
                            enabled: e.target.checked,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteAdType(r.placementKey)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Add ad type
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2}>
              <TextField
                size="small"
                label="Key"
                placeholder="e.g. popup"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
              <TextField
                size="small"
                label="Label"
                placeholder="Popup ads"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={addAdType}
                disabled={saving}
              >
                Add
              </Button>
            </Stack>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveRules}
              disabled={saving}
            >
              Save ad types
            </Button>
          </Paper>
        ) : tab === 2 ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Legal-first: spinner is a promotional &quot;reveal&quot; only — not a bet,
              lottery, or jackpot. Edit copy anytime; no code deploy needed.
            </Alert>
            {ui && (
              <Stack spacing={2} maxWidth={560}>
                <Alert severity="success" sx={{ mb: 0 }}>
                  <strong>Wheel + auto coupons + Bonus Pick</strong> (default ON):
                  after an ad, citizen sees the wheel → random + ad-owner coupons →
                  Congrats → optional <strong>Bonus Pick</strong> (Find Your Coupon →
                  2nd ad → claim). This does not replace Locsy Spin.
                </Alert>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ui.wheelBonusPickEnabled !== false}
                      onChange={(e) => {
                        const on = e.target.checked;
                        if (!on) {
                          const ok = window.confirm(
                            'Turn OFF Wheel + Bonus Pick?\n\n' +
                              'Citizens lose the guaranteed wheel allotment path and Bonus Pick. ' +
                              'Offer chances become less predictable (pool / old rules only). ' +
                              'Keep ON unless you are testing rollback.',
                          );
                          if (!ok) return;
                        }
                        setUi({ ...ui, wheelBonusPickEnabled: on });
                      }}
                    />
                  }
                  label="Wheel + auto coupons + Bonus Pick (master)"
                />
                {ui.wheelBonusPickEnabled === false && (
                  <Alert severity="warning">
                    Master is OFF — ad path may skip the intended Congrats / Bonus Pick
                    experience. Probability of a clear coupon allotment is lower when
                    pool rules alone decide. Prefer leaving this ON.
                  </Alert>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={ui.spinnerEnabled !== false}
                      onChange={(e) =>
                        setUi({ ...ui, spinnerEnabled: e.target.checked })
                      }
                    />
                  }
                  label="Spinner reveal UI ON"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={ui.worthFallbackEnabled !== false}
                      onChange={(e) =>
                        setUi({ ...ui, worthFallbackEnabled: e.target.checked })
                      }
                    />
                  }
                  label="One-tier worth fall back when pool empty"
                />
                <TextField
                  label="Spinner title"
                  value={ui.spinnerTitle || ''}
                  onChange={(e) =>
                    setUi({ ...ui, spinnerTitle: e.target.value })
                  }
                  fullWidth
                />
                <TextField
                  label="Legal / promo disclaimer"
                  value={ui.spinnerDisclaimer || ''}
                  onChange={(e) =>
                    setUi({ ...ui, spinnerDisclaimer: e.target.value })
                  }
                  fullWidth
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Global daily citizen coupon cap"
                  type="number"
                  value={ui.globalDailyCitizenCap ?? 8}
                  onChange={(e) =>
                    setUi({
                      ...ui,
                      globalDailyCitizenCap: Number(e.target.value),
                    })
                  }
                  helperText="Applies across all ad types (default 8)"
                  sx={{ maxWidth: 280 }}
                />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveUi}
                  disabled={saving}
                >
                  Save spinner & caps
                </Button>
              </Stack>
            )}
          </Paper>
        ) : (
          <Paper sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Find Your Coupon search powers <strong>Bonus Pick</strong> after the
              wheel (and related search APIs). It no longer replaces Locsy Spin on
              ad views — use <strong>Spinner &amp; caps → Wheel + Bonus Pick</strong>{' '}
              for the ad path. Keep rewarded-ad-before-claim ON so Bonus Pick shows
              a 2nd ad before issuing.
            </Alert>
            <Stack spacing={2} maxWidth={560}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(discovery.smart_coupon_discovery_enabled)}
                    onChange={(e) =>
                      patchDiscovery({
                        smart_coupon_discovery_enabled: e.target.checked,
                      })
                    }
                  />
                }
                label="Enable Find Your Coupon search (Bonus Pick)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={discovery.similar_enabled !== false}
                    onChange={(e) =>
                      patchDiscovery({ similar_enabled: e.target.checked })
                    }
                  />
                }
                label="Enable similar coupons fallback"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={discovery.notify_me_enabled !== false}
                    onChange={(e) =>
                      patchDiscovery({ notify_me_enabled: e.target.checked })
                    }
                  />
                }
                label="Enable Notify Me when unavailable"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={discovery.rewarded_ad_before_claim !== false}
                    onChange={(e) =>
                      patchDiscovery({
                        rewarded_ad_before_claim: e.target.checked,
                      })
                    }
                  />
                }
                label="Rewarded ad required before claim"
              />
              <TextField
                label="Top Worth count"
                type="number"
                value={discovery.top_worth_count ?? 12}
                onChange={(e) =>
                  patchDiscovery({ top_worth_count: Number(e.target.value) })
                }
                sx={{ maxWidth: 200 }}
              />
              <TextField
                label="Processing duration (ms)"
                type="number"
                value={discovery.processing_duration_ms ?? 2800}
                onChange={(e) =>
                  patchDiscovery({
                    processing_duration_ms: Number(e.target.value),
                  })
                }
                sx={{ maxWidth: 240 }}
              />
              <TextField
                label="Max similar coupons"
                type="number"
                value={discovery.max_similar ?? 5}
                onChange={(e) =>
                  patchDiscovery({ max_similar: Number(e.target.value) })
                }
                sx={{ maxWidth: 200 }}
              />
              <TextField
                label="Default citizen radius (km)"
                type="number"
                value={discovery.default_citizen_radius_km ?? 2}
                onChange={(e) =>
                  patchDiscovery({
                    default_citizen_radius_km: Number(e.target.value),
                  })
                }
                helperText="Matches shops Near You default (2 km)"
                sx={{ maxWidth: 280 }}
              />
              <TextField
                label="Max citizen radius (km)"
                type="number"
                value={discovery.max_citizen_radius_km ?? 25}
                onChange={(e) =>
                  patchDiscovery({
                    max_citizen_radius_km: Number(e.target.value),
                  })
                }
                sx={{ maxWidth: 280 }}
              />
              <TextField
                label="Default Mitra service radius (km)"
                type="number"
                value={discovery.default_mitra_service_radius_km ?? 10}
                onChange={(e) =>
                  patchDiscovery({
                    default_mitra_service_radius_km: Number(e.target.value),
                  })
                }
                helperText="Used when Mitra leaves service radius blank"
                sx={{ maxWidth: 320 }}
              />
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveDiscovery}
                disabled={saving}
              >
                Save Discovery settings
              </Button>
            </Stack>
          </Paper>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default CouponManagementAdmin;
